import React, { useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import "./App.css";
import { initLeadAttribution, trackPageView } from "./utils/leadAttribution";
import HomePage from "./pages/home.js";
import QuotePage from "./pages/quote";
import TermsPage from "./pages/terms";
import LocationRouter from "./pages/LocationRouter";
import CountryPage from "./pages/CountryPage";
import InternationalCityPage from "./pages/InternationalCityPage";
import ServicePage from "./pages/ServicePage";
import NotFound from "./pages/NotFound";
import ServicesPage from "./pages/ServicesPage";
import AsianaBridalShow from "./pages/AsianaBridalShow";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Helmet } from "react-helmet";

const theme = createTheme({
  palette: {
    primary: {
      main: "#5C005D",
      light: "#89208A",
      dark: "#3C003E",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#250035",
      light: "#4D1A60",
      dark: "#180024",
      contrastText: "#E0D7F5",
    },
    gold: {
      main: "#ffd700",
      light: "#ffdf33",
      dark: "#ccac00",
      contrastText: "#1a0a2e",
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
    },
    text: {
      primary: "#1A1A1A",
      secondary: "#444444",
    },
    error: {
      main: "#B00020",
    },
    warning: {
      main: "#FF9800",
    },
    info: {
      main: "#2196F3",
    },
    success: {
      main: "#4CAF50",
    },
  },
  typography: {
    fontFamily: '"EB Garamond SC", serif',
    h1: {
      fontFamily: '"EB Garamond SC", serif',
      fontWeight: 600,
    },
    h2: {
      fontFamily: '"EB Garamond SC", serif',
      fontWeight: 600,
    },
    h3: {
      fontFamily: '"EB Garamond SC", serif',
      fontWeight: 500,
    },
    h4: {
      fontFamily: '"EB Garamond SC", serif',
      fontWeight: 500,
    },
    h5: {
      fontFamily: '"EB Garamond SC", serif',
      fontWeight: 500,
    },
    h6: {
      fontFamily: '"EB Garamond SC", serif',
      fontWeight: 500,
    },
    body1: {
      fontFamily: '"EB Garamond SC", serif',
      fontWeight: 400,
    },
    body2: {
      fontFamily: '"EB Garamond SC", serif',
      fontWeight: 400,
    },
    button: {
      fontFamily: '"EB Garamond SC", serif',
      fontWeight: 600,
      textTransform: 'none',
    },
    caption: {
      fontFamily: '"EB Garamond SC", serif',
      fontWeight: 400,
    },
    overline: {
      fontFamily: '"EB Garamond SC", serif',
      fontWeight: 400,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "30px",
          padding: "10px 24px",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-2px)",
          },
        },
        contained: {
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          "&:hover": {
            boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
          },
        },
        outlined: {
          borderWidth: "2px",
          "&:hover": {
            borderWidth: "2px",
          },
        },
      },
    },
  },
});

// Evaluated once at module load. True only inside react-snap's headless Chrome.
const isPrerendering =
  typeof navigator !== 'undefined' &&
  navigator.userAgent.includes('ReactSnap');

const SITE_URL = "https://imperialdrummers.com";

const DEFAULT_TITLE =
  "UK Dhol Drummers | Professional Dhol Players for Weddings | Imperial Drummers";
const DEFAULT_DESCRIPTION =
  "Book professional UK dhol drummers for Asian weddings and events. Imperial Drummers offers authentic Punjabi dhol players in London, Birmingham, Manchester & nationwide. Trusted dhol entertainment for Indian weddings, Sikh weddings & celebrations. Available worldwide.";
const DEFAULT_OG_DESCRIPTION =
  "Book professional UK dhol drummers for Asian weddings and events. Authentic Punjabi dhol players in London, Birmingham, Manchester & nationwide. Available worldwide.";
const DEFAULT_KEYWORDS =
  "UK dhol drummers, dhol players UK, dhol players for weddings, Asian wedding entertainment, Indian wedding drummers, Punjabi dhol players, dhol hire UK, wedding dhol players London, dhol drummers Birmingham, dhol players Manchester, dhol entertainment Leicester, dhol players near me, Asian wedding dhol, Sikh wedding drummers, Hindu wedding entertainment, baraat dhol players, wedding entertainment UK, dhol booking, professional dhol players, Indian drummers for hire";

// Site-wide head defaults. The matching tags in public/index.html carry
// data-react-helmet so Helmet replaces them rather than adding a second copy
// next to them; without that, every pre-rendered page kept the homepage
// description and canonical ahead of its own. Pages override any of these by
// rendering their own <Helmet> (the deepest one wins per tag). Must live
// inside <Router> to read the current path.
function DefaultSeo() {
  const { pathname } = useLocation();
  const url = SITE_URL + pathname;

  return (
    <Helmet>
      <title>{DEFAULT_TITLE}</title>
      <meta name="description" content={DEFAULT_DESCRIPTION} />
      <meta name="keywords" content={DEFAULT_KEYWORDS} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={DEFAULT_TITLE} />
      <meta property="og:description" content={DEFAULT_OG_DESCRIPTION} />
    </Helmet>
  );
}

// Records how the visitor arrived and how much of the site they saw before
// enquiring. Must live inside <Router> to see route changes; renders nothing.
function AttributionTracker() {
  const location = useLocation();
  const started = useRef(false);

  useEffect(() => {
    if (isPrerendering) return;

    if (!started.current) {
      started.current = true;
      // The first load is where document.referrer and any ?utm_* parameters
      // still exist, and it already counts as page view one.
      initLeadAttribution();
      return;
    }
    trackPageView();
  }, [location.pathname]);

  return null;
}

function App() {
  // Enable animations only after React has committed its first render.
  // Pre-rendered HTML has class="no-anim" on <html> which suppresses all
  // CSS animations, preventing the "animate → hydration flash → re-animate" cycle.
  //
  // IMPORTANT: skip this during react-snap so that the saved HTML keeps the
  // no-anim class. Without this guard, react-snap removes no-anim before
  // saving, causing animations to fire before React loads on real page visits,
  // and then fire a second time when React updates Emotion class names during
  // hydration — producing the visible double-animation flash.
  useEffect(() => {
    if (isPrerendering) return;
    document.documentElement.classList.remove('no-anim');
    // Reveal content hidden by inline script for route-mismatched pre-renders
    const hideEl = document.getElementById('__snap-hide');
    if (hideEl) hideEl.remove();
    // Remove the loading screen shown when the page wasn't pre-rendered
    const splashEl = document.getElementById('__splash');
    if (splashEl) splashEl.remove();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      {/* Hydration version marker — index.js checks for this before calling hydrateRoot.
          Increment BUILD_VERSION whenever a deploy changes the pre-rendered HTML structure
          so stale pre-renders fall back to client rendering instead of throwing error #418. */}
      <span id="__build_v3" style={{ display: 'none' }} aria-hidden="true" />
      <Router>
        <DefaultSeo />
        <AttributionTracker />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/quote/:quoteId" element={<QuotePage />} />
          <Route path="/quote/:quoteId/:stage" element={<QuotePage />} />
          {/* Quote-scoped terms: the standard terms plus any clauses recorded
              against that quote. Kept off /quote/:quoteId/terms, which the
              /quote/:quoteId/:stage route above would treat as a wizard stage. */}
          <Route path="/terms/:quoteId" element={<TermsPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/exhibitions" element={<AsianaBridalShow />} />
          {/* Service pages */}
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:serviceSlug" element={<ServicePage />} />
          {/* International location pages */}
          <Route path="/dhol-players/:continentSlug/:countrySlug/:citySlug" element={<InternationalCityPage />} />
          <Route path="/dhol-players/:continentSlug/:countrySlug" element={<CountryPage />} />
          {/* Continent or UK city - LocationRouter disambiguates */}
          <Route path="/dhol-players/:slug" element={<LocationRouter />} />
          {/* Catch-all 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
